/**
 * My Certifications Page - All certification-type training records for the current user
 */

import { useState, useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { format, differenceInDays, isPast, parseISO } from 'date-fns'
import { learningApi } from '../api/learningApi'
import type { EmployeeTrainingListItem, VerificationStatus } from '../types/learning.types'

const LIMIT = 50
const EXPIRY_SOON_DAYS = 60

type ExpiryStatus = 'valid' | 'expiring' | 'expired' | 'no_expiry'
type SortField = 'training_name' | 'certification_date' | 'certification_expiry_date' | 'verification_status'
type SortOrder = 'asc' | 'desc'

function getExpiryStatus(item: EmployeeTrainingListItem): ExpiryStatus {
  if (!item.certification_expiry_date) return 'no_expiry'
  const expiry = parseISO(item.certification_expiry_date)
  if (isPast(expiry)) return 'expired'
  if (differenceInDays(expiry, new Date()) <= EXPIRY_SOON_DAYS) return 'expiring'
  return 'valid'
}

const EXPIRY_STATUS_CONFIG: Record<ExpiryStatus, { label: string; badge: string }> = {
  valid:     { label: 'Valid',         badge: 'bg-green-100 text-green-700' },
  expiring:  { label: 'Expiring Soon', badge: 'bg-amber-100 text-amber-700' },
  expired:   { label: 'Expired',       badge: 'bg-red-100 text-red-700' },
  no_expiry: { label: 'No Expiry',     badge: 'bg-gray-100 text-gray-600' },
}

const VERIFICATION_STATUS_COLORS: Record<VerificationStatus, string> = {
  pending:  'bg-amber-100 text-amber-700',
  verified: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
}

const SortIcon = ({ active, order }: { active: boolean; order: SortOrder }) => {
  if (!active) {
    return (
      <svg className="w-3.5 h-3.5 text-gray-300 group-hover:text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
      </svg>
    )
  }
  return order === 'asc' ? (
    <svg className="w-3.5 h-3.5 text-red-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
    </svg>
  ) : (
    <svg className="w-3.5 h-3.5 text-red-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  )
}

const MyCertificationsPage = () => {
  const navigate = useNavigate()

  const [expiryFilter, setExpiryFilter] = useState<'all' | ExpiryStatus>('all')
  const [verificationFilter, setVerificationFilter] = useState<VerificationStatus | ''>('')
  const [page, setPage] = useState(1)
  const [sortField, setSortField] = useState<SortField>('certification_expiry_date')
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc')

  const queryParams = useMemo(() => ({
    page,
    limit: LIMIT,
    verification_status: verificationFilter || undefined,
    include_deleted_training: false,
  }), [page, verificationFilter])

  const { data, isLoading } = useQuery({
    queryKey: ['myCertifications', queryParams],
    queryFn: () => learningApi.getMyRecords(queryParams),
  })

  const allRecords = data?.data || []

  // Filter to certification type only
  const certRecords = allRecords.filter(r => r.training.training_type === 'certification')

  // Summary counts (unaffected by expiry filter)
  const summary = {
    total: certRecords.length,
    valid: certRecords.filter(r => getExpiryStatus(r) === 'valid').length,
    expiring: certRecords.filter(r => getExpiryStatus(r) === 'expiring').length,
    expired: certRecords.filter(r => getExpiryStatus(r) === 'expired').length,
    no_expiry: certRecords.filter(r => getExpiryStatus(r) === 'no_expiry').length,
  }

  // Apply expiry filter
  const filtered = expiryFilter === 'all'
    ? certRecords
    : certRecords.filter(r => getExpiryStatus(r) === expiryFilter)

  // Client-side sort
  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      let cmp = 0
      switch (sortField) {
        case 'training_name':
          cmp = a.training.name.localeCompare(b.training.name)
          break
        case 'certification_date':
          cmp = (a.certification_date ?? '').localeCompare(b.certification_date ?? '')
          break
        case 'certification_expiry_date': {
          // no_expiry sorts last regardless of direction
          const aHas = !!a.certification_expiry_date
          const bHas = !!b.certification_expiry_date
          if (!aHas && !bHas) { cmp = 0; break }
          if (!aHas) { cmp = 1; break }
          if (!bHas) { cmp = -1; break }
          cmp = (a.certification_expiry_date ?? '').localeCompare(b.certification_expiry_date ?? '')
          break
        }
        case 'verification_status':
          cmp = a.verification_status.localeCompare(b.verification_status)
          break
      }
      return sortOrder === 'asc' ? cmp : -cmp
    })
  }, [filtered, sortField, sortOrder])

  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortOrder(o => o === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortOrder('asc')
    }
  }

  const hasActiveFilters = expiryFilter !== 'all' || verificationFilter !== ''

  const ThSortable = ({ field, children, className = '' }: { field: SortField; children: React.ReactNode; className?: string }) => (
    <th
      className={`px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer group hover:bg-gray-100 select-none transition-colors ${className}`}
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center gap-1.5">
        {children}
        <SortIcon active={sortField === field} order={sortOrder} />
      </div>
    </th>
  )

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-red-50/30">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-600 via-red-700 to-rose-700 px-6 py-8 shadow-lg">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-2 text-red-200 text-sm mb-2">
            <Link to="/learning" className="hover:text-white transition-colors">Learning</Link>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <span className="text-white">My Certifications</span>
          </div>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">My Certifications</h1>
              <p className="text-green-200">Track your professional certifications and expiry dates</p>
            </div>
            <Link
              to="/learning/records/new"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-white text-red-700 font-medium rounded-xl hover:bg-red-50 transition-all shadow-lg shrink-0"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Certification
            </Link>
          </div>

          {/* Summary strip */}
          {!isLoading && (
            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { label: 'Total', value: summary.total, color: 'bg-white/10 text-white' },
                { label: 'Valid', value: summary.valid, color: 'bg-red-900/30 text-red-100' },
                { label: 'Expiring Soon', value: summary.expiring, color: summary.expiring > 0 ? 'bg-amber-500/20 text-amber-100' : 'bg-white/10 text-white' },
                { label: 'Expired', value: summary.expired, color: summary.expired > 0 ? 'bg-red-500/20 text-red-100' : 'bg-white/10 text-white' },
              ].map(s => (
                <div key={s.label} className={`rounded-xl px-4 py-3 ${s.color}`}>
                  <p className="text-2xl font-bold">{s.value}</p>
                  <p className="text-xs opacity-80">{s.label}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 -mt-6 pb-8">
        {/* Expiry warning banner */}
        {summary.expiring > 0 && (
          <div className="mb-4 bg-amber-50 border border-amber-200 rounded-xl px-5 py-4 flex items-center gap-3">
            <svg className="w-5 h-5 text-amber-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <p className="text-sm text-amber-800">
              <span className="font-semibold">{summary.expiring} certification{summary.expiring !== 1 ? 's' : ''}</span> will expire within the next {EXPIRY_SOON_DAYS} days. Consider renewing them.
            </p>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-4">
          <div className="flex flex-wrap gap-3 items-center">
            {/* Expiry status filter pills */}
            <div className="flex items-center gap-1 bg-gray-100 rounded-xl p-1">
              {(['all', 'valid', 'expiring', 'expired', 'no_expiry'] as const).map(s => (
                <button
                  key={s}
                  onClick={() => setExpiryFilter(s)}
                  className={`px-3 py-1.5 text-sm rounded-lg font-medium transition-colors ${
                    expiryFilter === s ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {s === 'all' ? 'All' : s === 'no_expiry' ? 'No Expiry' : EXPIRY_STATUS_CONFIG[s].label}
                  {s !== 'all' && (
                    <span className="ml-1.5 text-xs text-gray-400">
                      ({s === 'valid' ? summary.valid : s === 'expiring' ? summary.expiring : s === 'expired' ? summary.expired : summary.no_expiry})
                    </span>
                  )}
                </button>
              ))}
            </div>

            <select
              value={verificationFilter}
              onChange={e => { setVerificationFilter(e.target.value as VerificationStatus | ''); setPage(1) }}
              className="px-4 py-2.5 bg-gray-50 border-0 rounded-xl focus:ring-2 focus:ring-red-500 text-gray-900 text-sm"
            >
              <option value="">All Verification Statuses</option>
              <option value="pending">Pending</option>
              <option value="verified">Verified</option>
              <option value="rejected">Rejected</option>
            </select>

            {hasActiveFilters && (
              <button
                onClick={() => { setExpiryFilter('all'); setVerificationFilter(''); setPage(1) }}
                className="px-4 py-2.5 text-sm text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
              >
                Clear filters
              </button>
            )}
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden mb-4">
          <div className="px-6 py-3 border-b border-gray-100">
            <span className="text-sm text-gray-500">
              {isLoading ? (
                <span className="inline-block w-28 h-4 bg-gray-100 rounded animate-pulse" />
              ) : (
                <><span className="font-semibold text-gray-900">{sorted.length}</span> certification{sorted.length !== 1 ? 's' : ''}</>
              )}
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="px-4 py-3 pl-6 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-8">#</th>
                  <ThSortable field="training_name" className="min-w-[200px]">Certification</ThSortable>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider min-w-[130px]">Provider</th>
                  <ThSortable field="certification_date" className="min-w-[120px]">Issued Date</ThSortable>
                  <ThSortable field="certification_expiry_date" className="min-w-[150px]">Expiry Date</ThSortable>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider min-w-[120px]">Expiry Status</th>
                  <ThSortable field="verification_status" className="min-w-[120px]">Verification</ThSortable>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider min-w-[70px]">Cert</th>
                  <th className="px-4 py-3 pr-6 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider min-w-[70px]">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {isLoading ? (
                  Array.from({ length: 6 }).map((_, i) => (
                    <tr key={i} className="animate-pulse">
                      <td className="px-4 py-3.5 pl-6"><div className="h-4 bg-gray-100 rounded w-5" /></td>
                      <td className="px-4 py-3.5"><div className="h-4 bg-gray-100 rounded w-44" /></td>
                      <td className="px-4 py-3.5"><div className="h-4 bg-gray-100 rounded w-28" /></td>
                      <td className="px-4 py-3.5"><div className="h-4 bg-gray-100 rounded w-24" /></td>
                      <td className="px-4 py-3.5"><div className="h-4 bg-gray-100 rounded w-28" /></td>
                      <td className="px-4 py-3.5"><div className="h-5 bg-gray-100 rounded-full w-20" /></td>
                      <td className="px-4 py-3.5"><div className="h-5 bg-gray-100 rounded-full w-18" /></td>
                      <td className="px-4 py-3.5"><div className="h-4 bg-gray-100 rounded w-8" /></td>
                      <td className="px-4 py-3.5 pr-6"><div className="h-4 bg-gray-100 rounded w-8" /></td>
                    </tr>
                  ))
                ) : sorted.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-6 py-16 text-center">
                      <svg className="w-12 h-12 mx-auto text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                      </svg>
                      <p className="text-gray-500 font-medium">No certifications found</p>
                      {hasActiveFilters ? (
                        <p className="text-gray-400 text-sm mt-1">Try adjusting your filters</p>
                      ) : (
                        <Link
                          to="/learning/records/new"
                          className="inline-flex items-center gap-2 mt-4 px-5 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors text-sm"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                          Add Your First Certification
                        </Link>
                      )}
                    </td>
                  </tr>
                ) : (
                  sorted.map((record, index) => {
                    const expStatus = getExpiryStatus(record)
                    const expConfig = EXPIRY_STATUS_CONFIG[expStatus]
                    const daysLeft = record.certification_expiry_date
                      ? differenceInDays(parseISO(record.certification_expiry_date), new Date())
                      : null

                    return (
                      <tr
                        key={record.id}
                        className="hover:bg-red-50/40 cursor-pointer transition-colors"
                        onClick={() => navigate(`/learning/records/${record.id}`)}
                      >
                        <td className="px-4 py-3.5 pl-6 text-sm text-gray-400">
                          {index + 1}
                        </td>
                        <td className="px-4 py-3.5">
                          <span className="font-medium text-gray-900 text-sm line-clamp-1">{record.training.name}</span>
                        </td>
                        <td className="px-4 py-3.5 text-sm text-gray-600">
                          {record.training.provider || <span className="text-gray-300">—</span>}
                        </td>
                        <td className="px-4 py-3.5 text-sm text-gray-600">
                          {record.certification_date
                            ? format(parseISO(record.certification_date), 'dd MMM yyyy')
                            : <span className="text-gray-300">—</span>}
                        </td>
                        <td className="px-4 py-3.5 text-sm">
                          {record.certification_expiry_date ? (
                            <div>
                              <span className={
                                expStatus === 'expired' ? 'text-red-600 font-medium' :
                                expStatus === 'expiring' ? 'text-amber-600 font-medium' :
                                'text-gray-600'
                              }>
                                {format(parseISO(record.certification_expiry_date), 'dd MMM yyyy')}
                              </span>
                              {daysLeft !== null && expStatus === 'expiring' && (
                                <span className="ml-1.5 text-xs text-amber-500">({daysLeft}d left)</span>
                              )}
                              {expStatus === 'expired' && daysLeft !== null && (
                                <span className="ml-1.5 text-xs text-red-400">({Math.abs(daysLeft)}d ago)</span>
                              )}
                              {expStatus === 'valid' && daysLeft !== null && (
                                <span className="ml-1.5 text-xs text-gray-400">({daysLeft}d)</span>
                              )}
                            </div>
                          ) : (
                            <span className="text-gray-400 text-xs">No expiry</span>
                          )}
                        </td>
                        <td className="px-4 py-3.5">
                          <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${expConfig.badge}`}>
                            {expConfig.label}
                          </span>
                        </td>
                        <td className="px-4 py-3.5">
                          <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${VERIFICATION_STATUS_COLORS[record.verification_status]}`}>
                            {record.verification_status.charAt(0).toUpperCase() + record.verification_status.slice(1)}
                          </span>
                        </td>
                        <td className="px-4 py-3.5">
                          {record.has_certificate ? (
                            <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                            </svg>
                          ) : (
                            <span className="text-gray-300 text-sm">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3.5 pr-6">
                          <button
                            onClick={e => { e.stopPropagation(); navigate(`/learning/records/${record.id}`) }}
                            className="text-red-600 hover:text-red-800 text-sm font-medium transition-colors"
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}

export default MyCertificationsPage
